/* How big a headline renders.
 *
 * ONE function, used by both the builder preview and the recording surface, so the two can
 * never disagree about what a round will look like — the same reason FakePage is shared.
 *
 * The result is applied as a CSS custom property, `--headline-scale`, and every headline rule
 * multiplies its own base size by it. That is what lets a single control drive two different
 * unit systems: the fake page sizes in container units (cqw) and the pre-reveal screen sizes in
 * viewport units (clamp(...vw...)), and neither has to be rewritten.
 */

export type HeadlineSize = 'small' | 'medium' | 'large';

export const HEADLINE_SIZES: HeadlineSize[] = ['small', 'medium', 'large'];

export const HEADLINE_SIZE_LABELS: Record<HeadlineSize, string> = {
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
};

/** What the round chose. Medium is 1, so an unset round renders exactly as it always has. */
const PRESET: Record<HeadlineSize, number> = {
  small: 0.85,
  medium: 1,
  large: 1.15,
};

/* Long headlines step down on their own.
 *
 * A fixed size cannot be right for both "Teen's Golf Ball Struck By Lightning" (36 chars) and
 * "Police Deploy Riot Shields To Rescue Runaway Goat Who Caused Havoc In Quiet Village" (83).
 * Without this, picking a size that suits the short ones lets the long ones run up against the
 * corner banner and the wordmark — which is the complaint that prompted the whole control.
 *
 * Thresholds measured against the real round: the 83-character goat headline is the longest in
 * play, and at 0.82 it clears the chrome comfortably even on Large.
 */
const AUTO_FIT: { over: number; scale: number }[] = [
  { over: 85, scale: 0.82 },
  { over: 60, scale: 0.9 },
];

export function autoFitScale(headline: string): number {
  const length = (headline || '').trim().length;
  for (const step of AUTO_FIT) {
    if (length > step.over) return step.scale;
  }
  return 1;
}

/**
 * Final multiplier for a headline: the round's preset, stepped down if the text is long.
 * Unknown or missing sizes fall back to medium rather than throwing — a bad value in the
 * database must not blank a page mid-recording.
 */
export function headlineScale(size: HeadlineSize | string | undefined | null, headline: string): number {
  const preset = PRESET[(size as HeadlineSize)] ?? PRESET.medium;
  return Number((preset * autoFitScale(headline)).toFixed(3));
}

/** Ready to spread onto a style prop. Kept here so no caller has to remember the var name. */
export function headlineScaleStyle(
  size: HeadlineSize | string | undefined | null,
  headline: string,
): React.CSSProperties {
  return { '--headline-scale': headlineScale(size, headline) } as React.CSSProperties;
}
